/**
 * OpsProjectsPage — role-scoped project list.
 * - Admin + Digital Ops see all projects.
 * - Track leaders see projects where they lead or are members.
 * Route: /ops/projects
 */
import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { opsApi, useOpsAuth } from './OpsAuthContext';
import OpsLayout from './OpsLayout';
import { PROJECT_STATUS, fmtDate } from './opsConstants';

const OpsProjectsPage = () => {
  const { user } = useOpsAuth();
  const [projects, setProjects] = useState([]);
  const [counts, setCounts] = useState({ total: 0 });
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const q = filter === 'all' ? '' : `?status=${filter}`;
      const { data } = await opsApi.get(`/projects${q}`);
      setProjects(data.projects);
      setCounts(data.counts);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const filterKeys = ['all', 'active', 'review', 'delivered', 'closed'];

  return (
    <OpsLayout title="المشاريع" subtitle="Projects Portfolio">
      <div className="flex flex-wrap items-center gap-2 mb-5">
        {filterKeys.map((k) => (
          <button
            key={k}
            data-testid={`project-filter-${k}`}
            onClick={() => setFilter(k)}
            className={`text-[12px] font-['Noto_Kufi_Arabic'] px-4 py-2 rounded-lg border transition ${
              filter === k
                ? 'bg-[#1F3D2B] text-white border-[#1F3D2B]'
                : 'bg-white text-[#1F3D2B] border-[#E6E0D6] hover:border-[#4A5D23]'
            }`}
          >
            {k === 'all' ? `الكل (${counts.total || 0})` : `${PROJECT_STATUS[k]?.label} (${counts[k] || 0})`}
          </button>
        ))}
      </div>

      {loading && (
        <p className="text-[13px] text-[#7A866E] font-['Noto_Kufi_Arabic']">جارٍ التحميل…</p>
      )}
      {!loading && projects.length === 0 && (
        <div
          data-testid="projects-empty"
          className="bg-white rounded-2xl ring-1 ring-[#E6E0D6] p-10 text-center"
        >
          <p className="font-['Noto_Kufi_Arabic'] text-[14px] text-[#7A866E]">
            لا توجد مشاريع مطابقة للفلتر الحالي
          </p>
        </div>
      )}

      <div className="grid gap-3">
        {projects.map((p) => (
          <Link
            key={p.id}
            to={`/ops/projects/${p.id}`}
            data-testid={`project-card-${p.code}`}
            className="bg-white rounded-2xl ring-1 ring-[#E6E0D6] hover:ring-[#4A5D23] transition p-5 flex items-start justify-between gap-4"
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-['Playfair_Display'] text-[16px] font-semibold text-[#1F3D2B]">
                  {p.code}
                </span>
                <span
                  className={`text-[10px] font-['Noto_Kufi_Arabic'] font-semibold px-2 py-0.5 rounded-full ${
                    PROJECT_STATUS[p.status]?.color || 'bg-gray-100'
                  }`}
                >
                  {PROJECT_STATUS[p.status]?.label || p.status}
                </span>
              </div>
              <p className="font-['Noto_Kufi_Arabic'] text-[15px] font-bold text-[#1F3D2B]">
                {p.client_name || '—'}
              </p>
              <p className="font-['Noto_Kufi_Arabic'] text-[12px] text-[#7A866E] mt-0.5">
                {p.client_org || 'بدون جهة'} · {p.service_type || '—'}
              </p>
              <p className="font-['Noto_Kufi_Arabic'] text-[11px] text-[#4A5D23] mt-2">
                القائد: {p.lead_email} · {p.member_emails?.length || 0} أعضاء · تم في {fmtDate(p.created_at)}
              </p>
            </div>
            <div className="text-end shrink-0">
              <div className="font-['Playfair_Display'] text-2xl font-semibold text-[#1F3D2B]">
                {p.progress || 0}%
              </div>
              <div className="w-24 h-1.5 bg-[#EFEAE1] rounded-full overflow-hidden mt-2">
                <div
                  className="h-full bg-[#4A5D23] transition-all"
                  style={{ width: `${p.progress || 0}%` }}
                />
              </div>
            </div>
          </Link>
        ))}
      </div>
    </OpsLayout>
  );
};

export default OpsProjectsPage;
